import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";
import triangle from "../../images/triangle-yellow.svg";
import mySplitMain from "../../images/mySplit-bg(2).png";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";

const eventOptions = [
  { label: "100m", value: "100" },
  { label: "200m", value: "200" },
];

export const AthleteCoachedPerformance = () => {
  const navigate = useNavigate();
  const distanceOptions = ["100m", "200m"];
  const { athlete } = useAthleteAuthContext();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [event, setEvent] = useState("100");
  const [open, setOpen] = useState(false);
  const [intensity, setIntensity] = useState("98");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const handleApplyFilter = async () => {
    if (!startDate || !endDate || !event) {
      alert("Please select Start Date, End Date, and Race Model Event");
      return;
    }

    setLoading(true);
    try {
      const url = `${API_URL.FRONTEND_COACHED_ATHLETE_GET_PERFORMANCE_DATA}?athlete_id=${athlete.id}&start_date=${startDate}&end_date=${endDate}&event=${event}&intensity=${intensity}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${athlete.token}` },
      });

      const json = await res.json();

      if (!res.ok) {
        console.error("Error fetching performance data:", json);
        return;
      }

      setRows(json.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---- Prepare chart data ---- */
  const chartData = rows
    .map((r) => ({
      distance: Number(r.distance),
      raceModel: r.race_model_time ? parseFloat(r.race_model_time) : null,
      actual: r.actual_time ? parseFloat(r.actual_time) : null,
    }))
    .sort((a, b) => a.distance - b.distance);

  /* ---- Tooltip formatting ---- */
  const tooltipFormatter = (value, name) => {
    if (value == null) return ["-", name];
    return [`${value.toFixed(2)} s`, name];
  };

  return (
    <AthleteLayout>
      <div className="flex flex-col h-full relative">
        <div className="bg-white">
          <AthleteHeader />
        </div>
        <div className="flex flex-col h-full relative bg-[#e6e6e6] -mt-2">
          <div className="flex-1 overflow-y-auto px-5 py-4 relative z-[2]">
            <div className="w-full max-w-2xl mx-auto flex flex-col space-y-6">
              {/* Header */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => navigate(-1)}
                  className="text-sm text-black hover:underline"
                >
                  ← Back
                </button>
              </div>

              {/* title */}
              <div className="flex justify-between">
                <p className="text-black uppercase text-2xl xs:text-3xl font-bold">
                  race model
                </p>

                <div className="flex flex-col">
                  <p className="bg-darkYellow text-black text-xs font-light text-center rounded-[4px] p-1">
                    RM
                  </p>
                  <p className="bg-black text-white text-xs font-light text-center rounded-[4px] p-1">
                    AT
                  </p>
                </div>
              </div>
              {/* Chart */}
              <div className="w-full h-64 bg-white/40 rounded-xl">
                {loading ? (
                  <div className="py-10 text-center text-gray-500">
                    Loading…
                  </div>
                ) : chartData.length === 0 ? (
                  <>
                    {/* <div className="py-10 text-center text-gray-500">
                  No data available
                </div> */}
                  </>
                ) : (
                  <>
                    {/* <p className="text-sm font-medium text-gray-700 mb-3">
                    {event}m Race Model vs Actual — {intensity}% Intensity
                  </p> */}

                    <div className="w-full h-64 p-3 mt-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{ top: 30, right: 20, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid
                            stroke="#E5E7EB"
                            strokeDasharray="2 6"
                          />

                          <XAxis
                            dataKey="distance"
                            label={{
                              // value: "",
                              position: "insideBottom",
                              offset: -5,
                            }}
                            tick={{ fill: "#111827", fontSize: 12 }}
                            axisLine={{ stroke: "#111827" }}
                            tickLine={false}
                            tickFormatter={(value) => `${value}m`}
                          />

                          {/* <YAxis
                          width={40}
                            label={{
                              angle: -90,
                              position: "insideLeft",
                            }}
                            tick={{ fill: "#111827", fontSize: 12 }}
                          /> */}

                          <Tooltip formatter={tooltipFormatter} />
                          {/* <Legend /> */}

                          <Line
                            type="monotone"
                            dataKey="raceModel"
                            name="Race Model"
                            stroke="#c19e42"
                            strokeWidth={2}
                            dot={{
                              r: 4,
                              fill: "#c19e42",
                              stroke: "#c19e42",
                              strokeWidth: 0,
                            }}
                          >
                            <LabelList
                              dataKey="raceModel"
                              position="bottom"
                              formatter={(value) => `${value}s`}
                              fill="#c19e42"
                              fontSize={10}
                              fontWeight={300}
                            />
                          </Line>

                          <Line
                            type="monotone"
                            dataKey="actual"
                            name="Actual"
                            stroke="#00000099"
                            strokeWidth={2}
                            strokeDasharray="3 3"
                            connectNulls
                            dot={{
                              r: 4,
                              fill: "#000000",
                              stroke: "#000000",
                              strokeWidth: 0,
                            }}
                          >
                            <LabelList
                              dataKey="actual"
                              position="top"
                              formatter={(value) => `${value}s`}
                              fill="#00000099"
                              fontSize={10}
                              fontWeight={300}
                            />
                          </Line>
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>

              {/* Filters */}
              <div>
                <div className="flex gap-y-3 bg-black rounded-xl py-3 xs:px-3">
                  <div className="w-1/3 border-r border-white/70 px-2">
                    <div className="relative flex flex-col gap-3">
                      {/* Trigger */}
                      <div
                        className="cursor-pointer flex flex-row items-center justify-center gap-2"
                        onClick={() => setOpen((prev) => !prev)}
                      >
                        <p className="text-center text-white text-xs xs:text-lg !leading-none font-medium capitalize">
                          Event
                        </p>
                        <img
                          src={triangle}
                          className={`w-3 transition-transform ${
                            open ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </div>

                      {/* Selected value */}
                      <div className="text-center">
                        <span className="text-white text-[10px] xs:text-sm font-light !leading-none bg-black border-none">
                          {eventOptions.find((o) => o.value === event)?.label}
                        </span>
                      </div>

                      {/* Dropdown */}
                      {open && (
                        <ul className="absolute top-full right-0 mt-2 w-full bg-black rounded-md shadow-lg z-10">
                          {eventOptions.map((option) => (
                            <li
                              key={option.value}
                              onClick={() => {
                                setEvent(option.value);
                                setOpen(false);
                              }}
                              className="px-3 py-2 text-xs text-darkYellow hover:bg-[#e6e6e6] cursor-pointer"
                            >
                              {option.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="w-1/3 border-r border-white/70 px-2">
                    <div className="text-center">
                      <p className="text-center text-white text-xs xs:text-lg !leading-none font-medium capitalize mb-2.5">
                        start date
                      </p>
                      <div className="relative w-full">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full text-white text-[10px] xs:text-sm font-light !leading-none placeholder:text-white bg-black border-none"
                        />
                        {!startDate && (
                          <span className="pointer-events-none bg-black w-[80%] absolute top-0 bottom-0 left-0 flex items-center justify-center text-white text-[10px] xs:text-sm font-light">
                            dd/mm/yy
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-1/3 px-2">
                    <div className="text-center">
                      <p className="text-white text-xs xs:text-lg !leading-none font-medium capitalize mb-2.5">
                        End Date
                      </p>
                      <div className="relative w-full">
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full text-white  text-[10px] xs:text-sm font-light !leading-none placeholder:text-white bg-black border-none"
                        />
                        {!endDate && (
                          <span className="pointer-events-none bg-black w-[80%] absolute top-0 bottom-0 left-0 flex items-center justify-center text-white text-[10px] xs:text-sm font-light">
                            dd/mm/yy
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Intensity */}
                <div className="mt-4 flex justify-center items-center gap-3">
                  <p className="text-xs xs:text-sm font-medium text-grayish">
                    Race Model Intensity
                  </p>

                  <div className="flex gap-6">
                    {["98", "95"].map((val) => (
                      <label
                        key={val}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="intensity"
                          value={val}
                          className="accent-black"
                          checked={intensity === val}
                          onChange={(e) => setIntensity(e.target.value)}
                        />
                        <span className="text-sm text-gray-700">{val}%</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* button */}
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={handleApplyFilter}
                    className="bg-black text-white px-4 py-2 rounded text-sm font-medium hover:bg-black/70 transition"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* background */}
          <div className="absolute left-1/2 -translate-x-1/2 top-20 xs:top-16 bottom-10 w-full xs:min-w-72 max-w-[430px] xs:max-w-[90%] h-auto z-0">
            <img
              src={mySplitMain}
              alt="mySplit"
              className="w-full xs:h-full object-contain"
            />
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};

export default AthleteCoachedPerformance;
